import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, Chip, Divider, Grid, Stack, Typography } from '@mui/material';

import { FormRoot, RenderFormField, SideModal } from '@/components';
import { withLoader, WithLoaderProps } from '@/hocs';
import { IPriorityForm } from '@/models';
import { useBuildingInspectionContext } from '@/pages/Tools/BuildingInspection/BuildingInspectionContext';
import { generateRandomId } from '@/pages/Tools/BuildingInspection/dummy_data/dummy_helper';
import {
  finishFailedInspectionFormFields,
  inspectionSendToFormFields,
  signedByInspectionFormFields,
} from '@/pages/Tools/BuildingInspection/dummy_data/dummy_inspection_form';
import { IVisit } from '@/pages/Tools/BuildingInspection/Models';
import { useToastStore } from '@/store';

interface IFinishInspectionFailedModalProps extends WithLoaderProps {
  openModal: string | undefined;
  setOpenModal: (selectedItemId: string | undefined) => void;
}
const FinishInspectionFailedModal = ({
  openModal,
  setOpenModal,
}: IFinishInspectionFailedModalProps) => {
  const { setInspectionVisits, buildingRecords, violations, visitNotes } =
    useBuildingInspectionContext();
  const { buildingId, inspectionId } = useParams();
  const navigate = useNavigate();
  const currentBuilding = buildingRecords.find(
    (record) => record._id === buildingId,
  );
  if (!currentBuilding) {
    return <p>Building not found</p>;
  }
  const currentViolations = violations.filter(
    (v) => v.inspection_id === inspectionId,
  );
  const currentVisitNotes = visitNotes.filter(
    (v) => v.inspection_id === inspectionId,
  );
  const { updateToast } = useToastStore();
  const methods = useForm<IPriorityForm>({
    defaultValues: {},
    mode: 'all',
  });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    if (openModal) {
      reset();
    }
  }, [openModal, reset]);

  const onSubmit = async (values: IPriorityForm) => {
    setInspectionVisits((prevVisit: IVisit[]) => {
      return [
        ...(prevVisit ? prevVisit : []),
        {
          _id: generateRandomId(),
          inspection_id: inspectionId,
          datePayed: new Date().toLocaleDateString(),
          violation_ids: currentViolations.map((v) => v._id),
          note_ids: currentVisitNotes.map((v) => v._id),
          status: 'Failed',
          ...values,
        } as unknown as IVisit,
      ];
    });
    setOpenModal(undefined);
    navigate(
      `/tools/building-inspection/${buildingId}/view_inspection/${inspectionId}`,
    );
    if (!inspectionId) {
      updateToast({
        message: 'invalid case id ',
        open: true,
      });
      return;
    }
  };

  return (
    <SideModal open={!!openModal} onClose={() => setOpenModal(undefined)}>
      <Stack
        sx={{
          bgcolor: 'white',
          height: '100%',
          p: 2,
          '.priorityComment textarea': {
            minHeight: 100,
          },
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1.5 }}>
          Finish inspection
        </Typography>
        <Divider />

        <FormProvider {...methods}>
          <FormRoot onSubmit={handleSubmit(onSubmit)} sx={{ height: '100%' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <h2>
                  Inspection will be set to{' '}
                  <Chip label="Failed" color="error" />
                </h2>
              </Grid>
              {finishFailedInspectionFormFields.map((field) => (
                <Grid key={field?.name} {...field?.grid} item>
                  <RenderFormField {...field} />
                </Grid>
              ))}
              <Grid item xs={12}>
                <h2>Inspection report signed by</h2>
              </Grid>
              {signedByInspectionFormFields.map((field) => (
                <Grid key={field?.name} {...field?.grid} item>
                  <RenderFormField {...field} />
                </Grid>
              ))}
              <Grid item xs={12}>
                <h2>Inspection report also emailed to</h2>
              </Grid>
              {inspectionSendToFormFields.map((field) => (
                <Grid key={field?.name} {...field?.grid} item>
                  <RenderFormField {...field} />
                </Grid>
              ))}
            </Grid>

            <Stack
              flexDirection="row"
              justifyContent="flex-end"
              alignSelf="flex-end"
              width="100%"
            >
              <Button
                variant="contained"
                color="inherit"
                size="medium"
                sx={{ mr: 2 }}
                onClick={() => setOpenModal(undefined)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                size="medium"
                onClick={handleSubmit(onSubmit)}
              >
                Save and lock
              </Button>
            </Stack>
          </FormRoot>
        </FormProvider>
      </Stack>
    </SideModal>
  );
};
export default withLoader(FinishInspectionFailedModal);
